import React from "react";
import { Card, Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import { useHistory } from "react-router-dom";
import { getComplianceIcon, getComplianceFrameworkBadgeText } from "../../../assets/images/compliance/IconCompliance.jsx";

const ComplianceBox = ({ name, percent, passed, failed, runId, scanDate, accountNumber, isEnabled = true, suppressed_checks = 0, suppressed_controls = 0 }) => {
  const history = useHistory();
  const iconSrc = getComplianceIcon(name);
  const frameworkBadgeText = getComplianceFrameworkBadgeText(name);

  const handleClick = () => {
    // Prevent navigation if compliance is not enabled
    if (!isEnabled) {
      return;
    }
    
    if (scanDate) {
      localStorage.setItem('selectedScanDate', scanDate);
    }
    if (percent !== undefined) {
      localStorage.setItem('selectedCompliancePercent', percent);
    }
    if (accountNumber) {
      localStorage.setItem('selectedAccountNumber', accountNumber);
    }
    history.push(`/compliance/controls?compliance=${name}&runId=${runId}&accountNumber=${accountNumber}`);
  };

  const hasSuppressions = (suppressed_checks > 0 || suppressed_controls > 0);

  // Custom tooltip content for suppressions
  const suppressionTooltipContent = (
    <Box
      sx={{
        p: 0,
        minWidth: 220,
        borderRadius: "10px",
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          padding: "16px 24px",
          borderBottom: "1px solid #E0E0E0",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: "#1a2233",
            fontSize: "0.875rem",
          }}
        >
          Suppression Metrics
        </Typography>
      </Box>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#6b7a90",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Suppressed Controls:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: "#1a2233",
              fontSize: "0.875rem",
            }}
          >
            {suppressed_controls || 0}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#6b7a90",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Suppressed Checks:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: "#1a2233",
              fontSize: "0.875rem",
            }}
          >
            {suppressed_checks || 0}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Card
      onClick={handleClick}
      sx={{
        minWidth: 280,
        maxWidth: 340,
        minHeight: 270,
        maxHeight: 270,
        p: 2.5,
        borderRadius: 3,
        boxShadow: "0px 2px 10px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.2,
        background: "#fff",
        transition: "box-shadow 0.2s, transform 0.2s",
        cursor: isEnabled ? "pointer" : "not-allowed",
        position: "relative",
        ...(isEnabled ? {} : {
          filter: "blur(0.5px)",
          opacity: 0.7,
        }),
        "&:hover": {
          boxShadow: isEnabled 
            ? "0px 6px 24px rgba(25, 118, 210, 0.12)"
            : "0px 2px 10px rgba(0,0,0,0.06)",
          transform: isEnabled ? "translateY(-2px) scale(1.03)" : "none",
        },
      }}
    >
      {/* Amber dot indicator for suppressions */}
      {hasSuppressions && isEnabled && (
        <Tooltip
          title={suppressionTooltipContent}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "transparent",
                p: 0,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
                borderRadius: "10px",
                border: "1px solid #E0E0E0",
                maxWidth: "none",
                "& .MuiTooltip-arrow": {
                  color: "#F5F5F5",
                  "&::before": {
                    border: "1px solid #E0E0E0",
                  },
                },
              },
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: "#f59e0b",
              border: "2px solid #fff",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
              zIndex: 1,
              cursor: "pointer",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                backgroundColor: "#f59e0b",
                opacity: 0.6,
                animation: "ripple 2s cubic-bezier(0.4, 0, 0.2, 1) infinite",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                backgroundColor: "#f59e0b",
                opacity: 0.4,
                animation: "ripple 2s cubic-bezier(0.4, 0, 0.2, 1) infinite 0.5s",
              },
              "@keyframes ripple": {
                "0%": {
                  transform: "translate(-50%, -50%) scale(1)",
                  opacity: 0.6,
                },
                "100%": {
                  transform: "translate(-50%, -50%) scale(2.5)",
                  opacity: 0,
                },
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </Tooltip>
      )}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 38,
        }}
      >
        {iconSrc ? (
          <img
            src={iconSrc}
            alt={name + " icon"}
            style={{ 
              height: 32, 
              width: "auto", 
              maxWidth: 40,
              filter: isEnabled ? "none" : "grayscale(50%)"
            }}
          />
        ) : frameworkBadgeText ? (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: isEnabled ? "#1976d2" : "#9e9e9e",
              letterSpacing: 0.5,
              fontSize: "1.1rem",
            }}
          >
            {frameworkBadgeText}
          </Typography>
        ) : (
          <Typography
            variant="h5"
            sx={{ 
              fontWeight: 800, 
              color: isEnabled ? "#1976d2" : "#9e9e9e", 
              letterSpacing: 1 
            }}
          >
            {name.charAt(0)}
          </Typography>
        )}
      </Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          textAlign: "center",
          mb: 1,
          color: isEnabled ? "#222" : "#9e9e9e",
          fontSize: "0.98rem",
          lineHeight: 1.15,
          minHeight: 40,
          maxHeight: 44,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          wordBreak: 'break-word',
        }}
      >
        {name}
      </Typography>

      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 96,
          height: 96,
          mb: 1.2,
        }}
      >
        <CircularProgress
          variant="determinate"
          value={100}
          size={96}
          thickness={4}
          sx={{ 
            color: isEnabled ? "#ef9a9a" : "#e0e0e0", 
            position: "absolute" 
          }}
        />
        <CircularProgress
          variant="determinate"
          value={percent}
          size={96}
          thickness={4}
          sx={{ 
            color: isEnabled 
              ? (percent === 0 ? "#ef9a9a" : "#a5d6a7")
              : "#e0e0e0"
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: isEnabled ? "#222" : "#9e9e9e",
              fontSize: percent === 100 ? "0.82rem" : "0.92rem",
              textAlign: "center",
              letterSpacing: 1.5,
              px: 1,
              background: "none",
              borderRadius: 0,
              minWidth: 0,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              overflow: "hidden",
            }}
          >
            {Number(percent).toFixed(2)}%
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: "auto" }}>
        <Typography
          variant="body2"
          sx={{ 
            color: isEnabled ? "#43a047" : "#9e9e9e", 
            fontWeight: 700, 
            fontSize: "1.05rem" 
          }}
        >
          Passed: {passed}
        </Typography>
        <Typography
          variant="body2"
          sx={{ 
            color: isEnabled ? "#e53935" : "#9e9e9e", 
            fontWeight: 700, 
            fontSize: "1.05rem" 
          }}
        >
          Failed: {failed}
        </Typography>
      </Box>
    </Card>
  );
};

export default ComplianceBox;
